import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MachineProvider } from "@/components/machines/MachineProvider";
import { Preloader } from "@/components/fx/Preloader";
import { Cursor, ScrollProgress } from "@/components/fx/Cursor";
import { BrewerAI } from "@/components/chat/BrewerAI";
import { initLenis, scrollToTarget, scrollToTop } from "@/lib/lenis";
import Home from "@/pages/Home";
import Equipment from "@/pages/Equipment";
import IndustriesPage from "@/pages/Industries";
import ServicePage from "@/pages/Service";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Partner from "@/pages/Partner";

const CURTAIN = [0.76, 0, 0.24, 1];

const Page = ({ children }) => (
  <>
    {children}
    <motion.div className="pointer-events-none fixed inset-0 z-[150] origin-top bg-ink-900" initial={{ scaleY: 1 }} animate={{ scaleY: 0, transition: { duration: 0.8, ease: CURTAIN, delay: 0.05 } }} exit={{ scaleY: 0 }} />
    <motion.div className="pointer-events-none fixed inset-0 z-[150] origin-bottom bg-ink-900" initial={{ scaleY: 0 }} animate={{ scaleY: 0 }} exit={{ scaleY: 1, transition: { duration: 0.55, ease: CURTAIN } }}>
      <div className="flex h-full items-center justify-center font-display text-[12vw] font-black uppercase tracking-[-0.04em] text-outline opacity-40">Brewer</div>
    </motion.div>
  </>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  const { pathname, hash } = location;
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => scrollToTarget(hash), 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(scrollToTop, 560);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  const el = (C) => <Page><C /></Page>;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={pathname}>
        <Route path="/" element={el(Home)} />
        <Route path="/equipment" element={el(Equipment)} />
        <Route path="/partner" element={el(Partner)} />
        <Route path="/industries" element={el(IndustriesPage)} />
        <Route path="/service" element={el(ServicePage)} />
        <Route path="/about" element={el(About)} />
        <Route path="/contact" element={el(Contact)} />
        <Route path="*" element={el(Home)} />
      </Routes>
    </AnimatePresence>
  );
};

const Progress = () => {
  const { scrollYProgress } = useScroll();
  const p = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  return <ScrollProgress progress={p} />;
};

function App() {
  useEffect(() => initLenis(), []);
  return (
    <div className="App">
      <BrowserRouter>
        <MachineProvider>
          <Preloader />
          <Progress />
          <div className="grain" aria-hidden />
          <Header />
          <main>
            <AnimatedRoutes />
          </main>
          <Footer />
          <BrewerAI />
          <Cursor />
          <Toaster theme="dark" position="top-center" />
        </MachineProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
